import { defineComponent } from 'vue'
import Container from 'typedi'
import Notification from '@/modules/common/services/notification.service'
import { mapState } from 'vuex'
import { PrintQueueItem, PrintLogStatus, BadgeSvgResponse } from '@/modules/level-two/models'
import ScriptX from '@/modules/common/components/ui-core/scriptx/scriptx.vue'
import MomentivePrint from '@/plugins/momentive-print/MomentivePrint.vue'
import { canReadBadgeSettings } from '@/modules/common/utils/badge-settings-permission.util'

/** Maximum length, in code points, of the document name sent to the print service. */
const MAX_DOCUMENT_NAME_LENGTH = 100
/** Runs of control characters (C0, DEL, line and paragraph separators). */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F\u2028\u2029]+/g

/**
 * Thrown (as a rejection) by resolveParticipantNotes when its response is stale: a later check
 * superseded it, the host was destroyed, or the caller cancelled. Callers must neither prompt nor print.
 */
export class NotesCheckSupersededError extends Error {
  constructor () {
    super('Notes check superseded')
    this.name = 'NotesCheckSupersededError'
    Object.setPrototypeOf(this, NotesCheckSupersededError.prototype)
  }
}

export default defineComponent({
  name: 'BadgePrintProviderMixin',

  components: {
    ScriptX,
    MomentivePrint
  },

  data () {
    return {
      printServiceEnabled: false,
      availablePrinters: [] as string[],
      selectedPrinter: null as string | null,
      notesWarningShow: false,
      notesWarningContent: '',
      notesWarningResolve: null as ((proceed: boolean) => void) | null,
      notesCheckSeq: 0,
      notesCheckInFlight: false,
      notesConfirming: false
    }
  },

  computed: {
    ...mapState('security', ['currentUser']),
    ...mapState('module', ['activatedEventModules']),
    ...mapState('badges', ['settings']),

    eventUuid (): string {
      return this.$route.params.event_uuid
    },

    badgesEnabledAndAllowed (): boolean {
      return (this as any).$hasPermission('badges_admin.badge_printing.view') && ((this as any).activatedEventModules?.BADGES?.enabled ?? false)
    },

    useMps (): boolean {
      return (this as any).settingBool('enable_momentive_print_service')
    },

    /** True while a notes fetch is in flight or the notes warning is open: every single-print entry point returns early on it. */
    notesCheckBusy (): boolean {
      return !!((this as any).notesCheckInFlight || (this as any).notesWarningShow || (this as any).notesConfirming)
    },

    providerComponent (): string {
      return (this as any).useMps ? 'MomentivePrint' : 'ScriptX'
    }
  },

  async mounted () {
    if (!canReadBadgeSettings((key: string) => (this as any).$hasPermission(key))) {
      return
    }
    if (!(this as any).settings || (this as any).settings.length === 0) {
      await this.$store.dispatch('badges/getSettings', { event_uuid: (this as any).eventUuid })
    }
  },

  beforeDestroy () {
    (this as any).cancelNotesCheck()
  },

  methods: {
    /**
     * Resolve the participant's current saved notes. Uses item.notes when the field is present
     * (including null or ''), otherwise makes exactly one fetch of the participant record.
     * Returns null on fetch failure (print without warning). Rejects with NotesCheckSupersededError
     * when the response is stale.
     */
    async resolveParticipantNotes (item: { participant_uuid: string; notes?: string | null }): Promise<string | null> {
      if (item.notes !== undefined) {
        return item.notes ?? null
      }
      const self = this as any
      const seq = ++self.notesCheckSeq
      let response: any
      self.notesCheckInFlight = true
      try {
        response = await self.$store.dispatch('attendee/fetchParticipantRecord', { event_uuid: self.eventUuid, participant_uuid: item.participant_uuid })
      } catch (error) {
        if (seq !== self.notesCheckSeq) {
          throw new NotesCheckSupersededError()
        }
        // eslint-disable-next-line no-console
        console.error(`Failed to fetch notes for participant ${item.participant_uuid}`, error)
        return null
      } finally {
        // only the current action clears the flag; a stale response must not end a newer action's loading state
        if (seq === self.notesCheckSeq) self.notesCheckInFlight = false
      }
      if (seq !== self.notesCheckSeq) {
        throw new NotesCheckSupersededError()
      }
      const notes = response?.participant_data?.notes
      return notes === undefined || notes === null ? null : String(notes)
    },

    /** Invalidate any in-flight notes check so its response is discarded. */
    cancelNotesCheck () {
      (this as any).notesCheckSeq++
      ;(this as any).notesCheckInFlight = false
    },

    /**
     * Show a notes-before-printing warning modal and return whether the user chose to proceed.
     * Resolves true if "Print Anyway" is clicked, false if "Cancel" is clicked.
     */
    promptNotesWarning (notes: string): Promise<boolean> {
      return new Promise(resolve => {
        (this as any).notesWarningContent = notes
        ;(this as any).notesWarningResolve = resolve
        ;(this as any).notesWarningShow = true
      })
    },

    /**
     * The single place the notes-before-printing decision lives: the setting gate, notes resolution,
     * trimmed-presence test and the warning prompt. Callers that are not a single print pass false.
     * Cancels any in-flight notes check first, so a late response from an earlier click can neither
     * prompt nor print. Resolves 'cancel' (without prompting) when the check was superseded.
     */
    async confirmSingleBadgePrint (item: { participant_uuid: string; notes?: string | null }, isSinglePrint: boolean): Promise<'proceed' | 'cancel'> {
      const self = this as any
      self.cancelNotesCheck()
      if (!isSinglePrint) return 'proceed'
      if (!self.settingBool('show_notes_warning_before_printing')) return 'proceed'
      let notes: string | null
      try {
        notes = await self.resolveParticipantNotes(item)
      } catch (error) {
        if (error instanceof NotesCheckSupersededError) return 'cancel'
        throw error
      }
      const trimmed = typeof notes === 'string' ? notes.trim() : ''
      if (!trimmed) return 'proceed'
      const proceed = await self.promptNotesWarning(notes)
      return proceed ? 'proceed' : 'cancel'
    },

    resolveNotesWarning (proceed: boolean) {
      (this as any).notesWarningShow = false
      const resolve = (this as any).notesWarningResolve
      if (resolve) {
        (this as any).notesWarningResolve = null
        resolve(proceed)
      }
    },

    /**
     * Look up a boolean-ish badges setting by key (e.g. '1' / 1 / true all count as on)
     * @param {string} key
     * @returns {boolean}
     */
    settingBool (key: string): boolean {
      const setting = ((this as any).settings || []).find((s: any) => s.key === key)
      return setting?.value === '1' || setting?.value === 1 || setting?.value === true
    },

    async initializePrintService () {
      const provider = this.$refs.provider as any
      try {
        this.printServiceEnabled = await provider.initialize()
      } catch (error) {
        this.printServiceEnabled = false
      }

      if (this.printServiceEnabled) {
        await this.fetchPrinters()
      }
    },

    /**
     * Get available printers and pre-select the service's resolved default, if any.
     */
    async fetchPrinters () {
      const provider = this.$refs.provider as any
      try {
        this.availablePrinters = await provider.getPrinters()
      } catch (error) {
        Container.get(Notification).error('Error getting available printers.')
        return
      }

      try {
        const defaultPrinter = await provider.getDefaultPrinter()
        if (defaultPrinter) {
          this.selectedPrinter = defaultPrinter
        }
      } catch (error) {
        // Non-fatal: leave the picker on its placeholder if the default can't be resolved.
      }
    },

    /**
     * Fetch participant badge
     * @param {string} participant_uuid
     * @returns {BadgeSvgResponse}
     */
    async fetchAttendeeBadgeSvg (participant_uuid: string): Promise<BadgeSvgResponse> {
      return await this.$store.dispatch('badges/fetchAttendeeBadgeSVG', {
        event_uuid: (this as any).eventUuid,
        participant_uuid
      })
    },

    /**
     * Converts SVG pixel dimensions to inches for 1:1 physical printing.
     * @param {string} svgString - The raw SVG source code.
     * @param {integer} dpi - The DPI the source was designed at (default 96).
     * @returns {string} - The modified SVG string with "in" units.
     */
    fixSvgDimensionsForPrinting (svgString: string, dpi = 96): string {
      const parser = new DOMParser()
      const doc = parser.parseFromString(svgString, 'image/svg+xml')
      const svgElement = doc.querySelector('svg')

      if (svgElement) {
        const currentWidth = parseFloat(svgElement.getAttribute('width') ?? '')
        const currentHeight = parseFloat(svgElement.getAttribute('height') ?? '')

        if (!isNaN(currentWidth) && !isNaN(currentHeight)) {
          const widthInInches = currentWidth / dpi
          const heightInInches = currentHeight / dpi

          svgElement.setAttribute('width', `${widthInInches}in`)
          svgElement.setAttribute('height', `${heightInInches}in`)
        }
      }

      return new XMLSerializer().serializeToString(svgElement as SVGSVGElement)
    },

    /**
     * Update the print log
     * @param {PrintLogStatus} logStatus
     * @param {string} participant_uuid
     */
    updatePrintLog (logStatus: PrintLogStatus, participant_uuid: string, printDate: string) {
      const data = {
        participant_uuid,
        printed_time: printDate,
        status: logStatus,
        properties: {
          workstation: 'PLATFORM_ADMIN',
          source: 'PLATFORM',
          participant_uuid,
          user: (this as any).currentUser?.uuid
        }
      }
      this.$store.dispatch('badges/createPrintLogEntry', { event_uuid: (this as any).eventUuid, data })
    },

    /**
     * Name recorded as the print job's document name: the item's full_name, else first and
     * last name joined by a space. Control characters are replaced by a space and the result is capped at
     * MAX_DOCUMENT_NAME_LENGTH code points. Returns '' when no name is available (the provider then
     * falls back to 'Badge').
     * @param {PrintQueueItem} item
     * @returns {string}
     */
    getPrintDocumentName (item: PrintQueueItem): string {
      const clean = (v: unknown): string => (typeof v === 'string' ? v.replace(CONTROL_CHARS, ' ').trim() : '')
      const fullName = clean(item.full_name)
      const name = fullName || [clean(item.first_name), clean(item.last_name)].filter(Boolean).join(' ')
      return Array.from(name).slice(0, MAX_DOCUMENT_NAME_LENGTH).join('').trimEnd()
    },

    /**
     * Print a single queue item: fetch its badge SVG if missing, fix its dimensions,
     * send it to the active provider, and log a successful print. Does not raise
     * notifications itself so callers (a bulk loop, a single search-and-print action)
     * can present errors in their own context.
     * @param {PrintQueueItem} item
     * @returns {{ success: boolean, stage?: 'fetch' | 'none' | 'print', message?: string }}
     */
    async printItem (item: PrintQueueItem): Promise<{ success: boolean, stage?: 'fetch' | 'none' | 'print', message?: string }> {
      const provider = this.$refs.provider as any
      let content = item.content

      if (!content) {
        try {
          const response = await this.fetchAttendeeBadgeSvg(item.participant_uuid as string)
          content = response?.svg
        } catch (error) {
          return { success: false, stage: 'fetch' }
        }
      }

      if (!content) {
        return { success: false, stage: 'none' }
      }

      try {
        const fixedSvg = this.fixSvgDimensionsForPrinting(content as string)
        const result = await provider.printOne(fixedSvg, this.selectedPrinter, this.getPrintDocumentName(item))

        if (result?.success) {
          const printDate = new Date().toUTCString()
          await this.$store.dispatch('attendee/postParticipantSimple', {
            event_uuid: (this as any).eventUuid,
            participant_uuid: item.participant_uuid,
            data: { badge_print_date: printDate }
          })
          this.updatePrintLog('PRINTED', item.participant_uuid as string, printDate)
          return { success: true }
        }

        return { success: false, stage: 'print', message: result?.message }
      } catch (error) {
        return { success: false, stage: 'print' }
      }
    }
  }
})
